import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

// Query params that should follow the user across all client-side
// navigation. The bugger feedback widget reads `?bugger` at page-load
// time; if React Router strips it on the next navigate/redirect, a
// refresh from the new URL loses the widget. Add other "session-debug"
// flags here as they come up.
const STICKY_PARAMS = ['bugger'] as const

function mergeSticky(to: string, current: URLSearchParams): string {
  const sticky = STICKY_PARAMS.filter((k) => current.has(k))
  if (sticky.length === 0) return to

  const [path = '', hash = ''] = to.split('#')
  const [pathname = '', qs = ''] = path.split('?')
  const target = new URLSearchParams(qs)
  for (const k of sticky) {
    // Don't clobber if the destination already specifies the param.
    if (!target.has(k)) target.set(k, current.get(k) ?? '')
  }
  const query = target.toString()
  return query ? `${pathname}?${query}${hash ? `#${hash}` : ''}` : to
}

// Drop-in replacement for react-router's useNavigate that forwards any
// active sticky params (`?bugger`) onto every push/replace.
export function useStickyNavigate() {
  const navigate = useNavigate()
  const { search } = useLocation()
  return useCallback(
    (to: string, opts?: { replace?: boolean }) => {
      const current = new URLSearchParams(search)
      navigate(mergeSticky(to, current), opts)
    },
    [navigate, search],
  )
}

// Static helper for the index redirect (which can't call hooks). Reads
// the current `window.location.search` synchronously at the moment the
// component renders.
export function withStickyParams(to: string): string {
  if (typeof window === 'undefined') return to
  return mergeSticky(to, new URLSearchParams(window.location.search))
}

// Returns the active sticky-params querystring as a "?..." prefix (or
// empty if none active). Use to append onto NavLink `to` strings inside
// the operations + admin layouts so sidebar clicks don't strip the param.
export function useStickySearch(): string {
  const { search } = useLocation()
  const current = new URLSearchParams(search)
  const out = new URLSearchParams()
  for (const k of STICKY_PARAMS) {
    if (current.has(k)) out.set(k, current.get(k) ?? '')
  }
  const s = out.toString()
  return s ? `?${s}` : ''
}
